/**
 * Component for Languages
 */

//  Generic imports
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import DOMPurify from "dompurify";

//  Motif imports
import MotifButton from "@ey-xd/motif-react/Button";
import {
  MotifTooltip,
  MotifCard,
  MotifCardHeader,
  MotifCardBody,
  MotifFormField,
  MotifToggleSwitch,
  MotifBadge,
  MotifInlineMessage,
} from "@ey-xd/motif-react";
import MotifToast from "@ey-xd/motif-react/Toast";

//  Icons imports
import iconInfo from "../../../assets/images/iconlanguage.svg";
import iconSelect from "../../../assets/images/iconselect.svg";
import iconReplace from "../../../assets/images/iconreplace.svg";
import iconTrash from "../../../assets/images/Icontrash.svg";
import iconInfoEmpty from "../../../assets/images/info-empty.svg";
import iconWarningCircleAmber from "../../../assets/images/warning_circle_amber.svg";
import iconProgressLoaderCircle from "../../../assets/images/Loading_indicator_circles.svg";

//  Internal imports
import "./Languages.scss";
import Utils from "../../../utils/Utils";
import LanguageList from "./LanguageList";
import {
  language_info,
  ai_warning,
  language_info_description,
  tooltipReplace,
} from "../../../assets/data/constants";
import LanguageService from "../../../services/languageService";
import SelectedText from "../../molecules/modals/selectedText/SelectedText";
import LanguageTransform from "../../molecules/modals/LanguageTransform/LanguageTransformModal";
import { setProjectLanguages, setActiveLanguage } from "../../../redux/userSlice";

/* global Office */

export default function Languages() {
  const { user, activeProject, activeLanguage } = useSelector((state) => state?.user);
  const dispatch = useDispatch();

  const projectId = activeProject?.project_UUID;
  const userEmail = user?.username;

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [isCancelVisible, setIsCancelVisible] = useState(false);
  const [isOutputGenerated, setIsOutputGenerated] = useState(false);
  const [isDiscardClicked, setIsDiscardClicked] = useState(false);
  const [isDiscardConfirmed, setIsDiscardConfirmed] = useState(false);
  const [isReplaceConfirmed, setIsReplaceConfirmed] = useState(false);
  const [selectedTextModalShow, setSelectedTextModalShow] = useState(false);
  const [showDiscardToast, setShowDiscardToast] = useState(false);
  const [isToggleClicked, setIsToggleClicked] = useState(false);
  const [languageOptions, setLanguageOptions] = useState({});
  const [languageInput, setLanguageInput] = useState("");
  const [languageOutput, setLanguageOutput] = useState("");
  const [isReplaceDisabled, setIsReplaceDisabled] = useState(true);
  const [noLanguagesFound, setNoLanguagesFound] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [languageTransform, setLanguageTransform] = useState({
    checkboxes: {
      improveLang: false,
      concise: false,
      translate: false,
    },
    dropdownValues: {
      sourceLang: "",
      targetLang: "",
    },
  });

  const utils = new Utils();
  const languageService = new LanguageService();
  
  /**
   * Function to get Languages Finesse
   */
  useEffect(() => {
    getLanguagesAsync();
    //  eslint-disable-next-line
  }, []);

  /**
   * Function to get all Languages Finesse
   */
  const getLanguagesAsync = async () => {
    setShowLoader(true);
    const vLanguages = await languageService.getLanguageFinesse(projectId, userEmail);
    if (!vLanguages || (vLanguages.length === 0)) {
        setNoLanguagesFound(true);
        setLanguages(null);
        dispatch(setProjectLanguages({
          languages: null
        }));
    } else {
      setNoLanguagesFound(false);
      setLanguages(vLanguages);
      dispatch(setProjectLanguages({
        languages: vLanguages
      }));
    }
    setShowLoader(false);
  };
  
  /**
   * Hook to add document selection change handler
   */
  useEffect(() => {
    if (Office.context.document && isButtonClicked) {
      Office.context.document.addHandlerAsync(
        "documentSelectionChanged",
        handleInsertContent,
        (res) => {}
      );
    }
    //  eslint-disable-next-line
  }, [isButtonClicked]);

  /**
   * Function to check whether placed cursor in PPT or selected any text
   * to display the conntent
   */
  const handleInsertContent = () => {
    try {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Text,
        (res) => {
          if (res.status === Office.AsyncResultStatus.Failed) {
            setIsButtonDisabled(true);
            setSelectedText('');
              setIsCancelVisible(false);
              setIsButtonClicked(true);
          } else {
            const text = res.value.trim();
            if (text && isButtonClicked) {
              setSelectedText(text);
              setIsButtonDisabled(false);
              setIsCancelVisible(true);
              setIsButtonClicked(false);
            } else {
              setSelectedText('');
              setIsButtonDisabled(true);
              setIsCancelVisible(false);
              setIsButtonClicked(true);
            }
          }
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function triggred when selected text button click
   */
  const handleButtonClick = () => {
    setIsButtonClicked(true);
    setIsButtonDisabled(true);
  };

  /**
   * Function for cancelling the selection
   */
  const handleCancelClick = () => {
    setSelectedText("");
    setIsButtonClicked(false);
    setIsButtonDisabled(true);
    setIsCancelVisible(false);
  };

  /**
   * Function for handle the discard button click
   */
  const discardClick = () => {
    setIsDiscardClicked(true);
    setSelectedTextModalShow(true);
  };

  /**
   * Function for handle the replace button click
   */
  const replaceClick = () => {
    setIsDiscardClicked(false);
    setSelectedTextModalShow(true);
  };

  /**
   * Function for handle the confirm button click when user is discarding or replacing
   */
  const selectedTextConfirm = async () => {
    if (isDiscardClicked) {
      const vData = { responseId: activeLanguage.responseId };
      const isDiscardedResponse = await languageService.setDiscardOrReplaceaLanguage(vData, 2);
      if (isDiscardedResponse) {
        getLanguagesAsync();
        clearActiveLanguage();
        setShowDiscardToast(true);
        setSelectedTextModalShow(false);
        setIsDiscardConfirmed(true);
        setSelectedText("");
        setIsOutputGenerated(false);
        setIsButtonDisabled(true);
        setIsCancelVisible(false);
      }
    } else {
      insertResponseToPowerpoint();
    }
  };

  /**
   * Function for Toggle to Show Original Text
   */
  const handleToggleClick = () => {
    setIsToggleClicked((prev) => !prev);
  };

  /**
   * Hook to add document selection change handler
   */
  useEffect(() => {
    if (Office.context.document) {
      Office.context.document.addHandlerAsync(
        "documentSelectionChanged",
        handleInsertVisibility,
        (res) => {}
      );
    }
    //  eslint-disable-next-line
  }, []);

  /**
   * Function to check whether placed cursor in PPT or selected any text
   * to enable or disable insert button
   */
  const handleInsertVisibility = () => {
    try {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Text,
        (res) => {
          if (res.status === Office.AsyncResultStatus.Failed) {
            setIsReplaceDisabled(true);
          } else {
            const text = res.value.trim();
            if (text !== "") {
              setIsReplaceDisabled(false);
            } else {
              setIsReplaceDisabled(true);
            }
          }
        }
      );
    } catch (error) {}
  };

  /**
   * Function for replace data into powerpoint on confirm button click
   */
  const insertResponseToPowerpoint = async () => {
    if (activeLanguage.responseText) {
      try {
        await Office.context.document.setSelectedDataAsync(
          activeLanguage.responseText,
          async (res) => {
            const vData = { responseId: activeLanguage.responseId };
            const response = await languageService.setDiscardOrReplaceaLanguage(vData, 1);
            if (response) {
              setTimeout(() => {
                getLanguagesAsync();
                clearActiveLanguage();
                setSelectedTextModalShow(false);
                setIsReplaceConfirmed(true);
                setSelectedText("");
                setIsOutputGenerated(false);
                setIsButtonDisabled(true);
                setIsCancelVisible(false);
              }, 1000);
            }
          }
        );
      } catch (error) {
        // Do nothing
      }
    }
  };

  const handleTransformLanguage = async () => {
    const vDateToday = new Date();
    const vFormattedDate = vDateToday.toISOString().slice(0, 10);
    const payload = {
      languageRequest: {
        projectId: projectId,
        projectType: parseInt(activeProject?.category) || 1,
        userAction: "language",
        created_by: user?.username,
        created_on: vFormattedDate,
        parameters: {
          userInput: selectedText,
          improveLang: languageTransform.checkboxes.improveLang,
          concise: languageTransform.checkboxes.concise,
          translate: {
            isSelected: languageTransform.checkboxes.translate,
            sourceLang: languageTransform.dropdownValues.sourceLang || "",
            targetLang: languageTransform.dropdownValues.targetLang || "",
          },
        },
      },
    };

    try {
      setIsOpenModal(false);
      setShowLoader(true);
      const response = await languageService.setTransFormLanguage(payload);
      if (response?.platformResponse) {
        setIsOutputGenerated(true);
        setLanguageInput(selectedText);
        setLanguageOutput(response.platformResponse);
        setLanguageOptions(languageTransform.checkboxes);
        dispatch(setActiveLanguage({
          language: {
            responseId: response.responseId,
            originalText: selectedText,
            improveLang: languageTransform.checkboxes.improveLang,
            concise: languageTransform.checkboxes.concise,
            translate: languageTransform.checkboxes.translate,
            sourceLang: languageTransform.dropdownValues.sourceLang,
            targetLang: languageTransform.dropdownValues.targetLang,
            responseText: response.platformResponse
          }
        }));
      } else {
        return false;
      }
      setSelectedText("");
      setShowLoader(false);
    } catch {
      setShowLoader(false);
    } finally {
      setShowLoader(false);
      clearAllSelections();
      setIsDiscardConfirmed(false);
      setIsReplaceConfirmed(false);
      setIsOpenModal(false);
    }
  };

  const clearAllSelections = () => {
    setLanguageTransform({
      checkboxes: Object.keys(languageTransform.checkboxes).reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {}
      ),
      dropdownValues: {},
    });
  };

  const clearActiveLanguage = () => {
    dispatch(setActiveLanguage({
      language: null
    }));
  }

  const handleProceed = () => {
    setIsOpenModal(true);
  };

  return (
    <>
      {
        /* Loader */
        showLoader &&
        (
          <div className="loaderDiv">
            <img
              src={iconProgressLoaderCircle}
              alt="Progress"
              className="refreshIcon "
            />{/*
            */}Generating
          </div>
        )
      }
      {
        /* No Languages info */
        (
          (!isOutputGenerated && (selectedText === "")) || 
          (isOutputGenerated && (isDiscardConfirmed || isReplaceConfirmed) && noLanguagesFound)
        ) &&
        (
          <div className="language-Tab">
            <div className="no-language">
              <div className="no-language-img">
                <img
                  src={iconInfo}
                  alt="language-empty"
                  className="iconInfo"
                />
              </div>
              <p className="language-info"> {language_info}</p>
              <div className="no-language-description">
                {language_info_description}
              </div>
            </div>
          </div>
        )
      }
      {
        /* Display languages */
        !noLanguagesFound && (selectedText === "") &&
        (
          <LanguageList languages={languages} />
        )
      }
      {
        /* Select text from Powerpoint */
        !isOutputGenerated && (selectedText !== "") && !showLoader &&
        (
          <div className="language-Tab">
            <div className="selected-text">
              <p className=" selecetd-text-language">{selectedText}</p>
            </div>
          </div>
        )
      }
      {
        /* Display generated respose */
        isOutputGenerated && !isDiscardConfirmed && !isReplaceConfirmed &&
        (
          <>
            <MotifInlineMessage
              variant="warning"
              icons={
                {
                  message: <img src={iconWarningCircleAmber} className="iconWarningInfoCircle" alt="Warning" />
                }
              }
              className="msgAIContainer"
            >
              <span className="msgAI">{ai_warning}</span>
            </MotifInlineMessage>
            <MotifCard className="language-box-container">
              <MotifCardHeader className="cardheader">
                <div className="backBtn">
                  <span className="backButtonText">
                    Language Finesse
                  </span>
                </div>
                <div className="badge-container">
                  {
                    languageOptions.translate &&
                    <MotifTooltip
                      trigger={
                        <MotifBadge
                          value={'T'}
                          variant="default"
                          className="translate-language-badge"
                        />
                      }
                      contentClassName="tooltipWithoutClose"
                      allowHover={true}
                      allowClick={false}
                      hideCloseButton={true}
                      placement="left"
                    >
                      Translate
                    </MotifTooltip>
                  }
                  {
                    languageOptions.concise &&
                    <MotifTooltip
                      trigger={
                        <MotifBadge
                          value={'C'}
                          variant="default"
                          className="concise-language-badge"
                        />
                      }
                      contentClassName="tooltipWithoutClose"
                      allowHover={true}
                      allowClick={false}
                      hideCloseButton={true}
                      placement="left"
                    >
                      Make more concise
                    </MotifTooltip>
                  }
                  {
                    languageOptions.improveLang &&
                    <MotifTooltip
                      trigger={
                        <MotifBadge
                          value={'I'}
                          variant="default"
                          className="improve-language-badge"
                        />
                      }
                      contentClassName="tooltipWithoutClose"
                      allowHover={true}
                      allowClick={false}
                      hideCloseButton={true}
                      placement="left"
                    >
                      Improve written language
                    </MotifTooltip>
                  }
                </div>
              </MotifCardHeader>
              {
                !isToggleClicked &&
                <MotifCardBody className="contentLanguageResponse"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(utils.markdownToHtml(languageOutput)) }}
                >
                </MotifCardBody>
              }
              {
                isToggleClicked &&
                <MotifCardBody className="contentLanguageResponse">
                  {languageInput}
                </MotifCardBody>
              }
            </MotifCard>
          </>
        )
      }
      <div className="genericFooter">
        {(!isOutputGenerated && !showLoader) ? (
          <div className="footerInner alignEnd languageTab-btn">
          {(selectedText === "") && (
            <MotifButton
              variant="secondary"
              onClick={() => handleButtonClick()}
              className={isButtonClicked ? "leftButtonselected" : "leftButton"}
            >
              <div className="icon-select">
                <img src={iconSelect} alt="select" className="iconSelect" />
                <span>Select Text</span>
              </div>
            </MotifButton>
          )}
          {isCancelVisible && (
            <div className="rightButtons">
              <MotifButton
                className="btnCancel"
                variant="secondary"
                onClick={handleCancelClick}
              >
                <span>Cancel</span>
              </MotifButton>
              <MotifButton
                onClick={() => handleProceed()}
                className="btnProceed"
                variant="primary"
                disabled={(selectedText === "") || isButtonDisabled}
              >
                <span>Proceed</span>
              </MotifButton>
            </div>
          )}
          {!isCancelVisible && (
            <MotifButton
              className="btnProceed"
              variant="primary"
              disabled={(selectedText === "") || isButtonDisabled}
            >
              <span>Proceed</span>
            </MotifButton>
          )}
        </div>
        ) : (
          <div className="footerInner">
            <div className="toggle">
              {!isToggleClicked ? (
                <span className="show-original">Show original</span>
              ) : (
                <span className="show-original">Hide original</span>
              )}
              <MotifFormField>
                <MotifToggleSwitch
                  id="exampleToggle"
                  checked={isToggleClicked}
                  onChange={() => {
                    handleToggleClick();
                  }}
                  hideLabels={true}
                  disabled={showLoader}
                />
              </MotifFormField>
            </div>
            <div className="rightButtons textEllipsis">
              <MotifButton
                className="btnDiscard"
                variant="secondary"
                onClick={() => discardClick()}
                disabled={showLoader}
              >
                <img src={iconTrash} alt="Document" className="Discard" />
                <span className="textEllipsis">Discard</span>
              </MotifButton>
              <MotifTooltip
                trigger={
                  <MotifButton
                    className="btnInsert"
                    variant="primary"
                    disabled={isReplaceDisabled || showLoader}
                    onClick={() => replaceClick()}
                  >
                    <img
                      src={iconReplace}
                      alt="Insert"
                      className="iconInsert"
                    />
                    <span className="textEllipsis">Replace</span>
                  </MotifButton>
                }
                contentClassName="tooltipWithoutCloseCenterText"
                allowHover={true}
                allowClick={false}
                hideCloseButton={true}
                placement="top"
                hide={!isReplaceDisabled}
              >
                {tooltipReplace}
              </MotifTooltip>
            </div>
          </div>
        )}
      </div>
      {selectedTextModalShow && (
        <div>
          <SelectedText
            selectedTextModalShow={selectedTextModalShow}
            isDiscardClicked={isDiscardClicked}
            setSelectedTextModalShow={setSelectedTextModalShow}
            selectedTextConfirm={selectedTextConfirm}
          />
        </div>
      )}
      {showDiscardToast && (
        <div className="toast-div">
          <MotifToast
            onClose={() => setShowDiscardToast(false)}
            icons={{ toast: <img src={iconInfoEmpty} alt="Info" /> }}
          >
            Selection discarded
          </MotifToast>
        </div>
      )}
      {isOpenModal && (
        <LanguageTransform
          openModal={isOpenModal}
          setOpenModal={setIsOpenModal}
          selectedText={selectedText}
          setIsOutputGenerated={setIsOutputGenerated}
          languageTransform={languageTransform}
          setLanguageTransform={setLanguageTransform}
          handleTransformLanguage={handleTransformLanguage}
          clearAllSelections={clearAllSelections}
        />
      )}
    </>
  );
}
